import { useDashboardMeContext } from '@/dashboard/me-context';
/**
 * CALLCENTRE-EOD-V1 -- the call-centre agent's Daily report
 * (/dashboard/callcenter/daily-report).
 *
 * The SLA / lead-chase slice of "Daily / EOD Call Centre reporting" (activity
 * 13) -- a DATE-NAVIGABLE report; "today" is simply the default day, never a
 * today-only screen. Server-fenced to call_center_agent (see the route
 * module header, api/routes/callcenter-daily-report.ts). Read-only: every
 * control here is a filter, nothing writes.
 *
 * REUSE: the backend composes this wire from getSlaLeakage + loadChaseQueue
 * (TODAY only, unchanged) plus one new day-bounded lead_events activity read
 * (any date) -- see docs/AGENT-RUNS/CALLCENTRE-EOD-V1.md for the full
 * data-layer reuse proof. This page renders that composition; it derives no
 * business fact of its own.
 *
 * SCOPE (stated on the surface, not just here): this report covers
 * lead-chase and SLA follow-up work only. Call volume and agent-performance
 * (telephony) reporting are NOT included -- they need a schema + writer this
 * package does not build and are gated on the dark Office24x7 webhook (a
 * founder/vendor act). Backlog counts (still-open chase, breached SLA) are a
 * LIVE, current-moment fact -- reconstructing them for a past day would need
 * a persisted daily snapshot this package does not add (see the module
 * header of lib/callcenter-daily-report.ts); a past day's report shows the
 * ACTIVITY actually logged that day instead, which is genuinely historical.
 *
 * PALETTE: docs/design/design-language/02-DESIGN-TOKENS.md section 10b
 * (RATIFIED 2026-08-25). No state chip from the 14-state matrix renders here
 * (this report's vocabulary -- SLA breach classes, chase classes, lead_events
 * labels -- has no member in that matrix). The urgency ramp's 'soon'/
 * 'breached' steps render SLA-breach severity, row-level text + icon only,
 * never a pill and never stacked with a second urgency signal per row.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { fetchCallcenterDailyReport } from '@/dashboard/api';
import {
  ACTIVITY_CATEGORY_LABEL,
  addBusinessDays,
  classMeta,
  dayLabel,
  formatLeakageAge,
  isRollupWire,
  isValidBusinessDate,
  proportionLabel,
  urgencyStepForSeverity,
  type ActivityEntryView,
  type ActivityTypeRowView,
  type CallcenterDailyReportWire,
  type CallcenterDailyRollupAgentRowView,
  type CallcenterDailyRollupWire,
  type ChaseClass,
  type ChaseClassRowView,
  type SlaClassSummaryView,
  type UrgencyStep,
} from './callcenter-daily-report-view';
import { CHASE_CLASS_LABEL } from '@/dashboard/lead-cockpit/CallcenterCommandCenter';
import {
  AcButton,
  AcSelect,
  DataTable,
  DateCommandBar,
  DateField,
  SelectorBar,
  DAY_SCOPE_QUERY_KEY,
  DrawerShell,
  EmptyState,
  ErrorState,
  KpiTile,
  LoadingState,
  PageHeader,
  QuietAction,
  SectionBand,
  SectionShell,
  type DataTableColumn,
} from '@/dashboard/ui';

export const CCDR_EXCLUSION_NOTE =
  'This report covers lead-chase and SLA follow-up work. Call volume and agent performance are not included here -- they are not zero, they are not yet built.';

export const CCDR_BACKLOG_LIVE_ONLY_NOTE =
  'Still-open and breached-SLA counts are a live, right-now fact -- they cannot be reconstructed for a past day without a saved daily snapshot, which this report does not keep. Switch to today to see the current backlog.';

// ---- Manager rollup (AGENT-DAY-PRODUCTIVITY-ROLLUP-V1, activity 11) --------
// The rollup lives on THIS SAME page, under a manager lens: the backend
// composes the leadership tier onto the ONE route (see the route module
// header), so a manager landing here sees this branch instead of the
// per-agent view below -- never a second page, never both at once.

export const CCDR_ROLLUP_ATTRIBUTION_NOTE =
  "Each row shows activity recorded today on leads currently assigned to that person -- not proof of who personally made the call or sent the message. A lead reassigned during the day carries its whole day to whoever holds it now.";

export const CCDR_ROLLUP_EXCLUSION_NOTE =
  'This rollup covers lead-chase and follow-up activity only. Call volume and agent performance (talk time, hold, wrap) are not included here -- they are vendor-gated and not yet built.';

interface AgentRollupRow {
  readonly key: string;
  readonly staffLabel: string;
  readonly totalCount: number;
  readonly distinctLeadsTouched: number;
  readonly isTotal?: boolean;
}

function agentRollupColumns(): DataTableColumn<AgentRollupRow>[] {
  return [
    { key: 'staffLabel', header: 'Agent', render: (r) => r.staffLabel },
    { key: 'totalCount', header: 'Logged this day', align: 'end', render: (r) => String(r.totalCount) },
    {
      key: 'distinctLeadsTouched',
      header: 'Distinct leads touched',
      align: 'end',
      render: (r) => String(r.distinctLeadsTouched),
    },
  ];
}

/** Per-agent rows, busiest-first (already the backend's own order) + a TOTAL
 *  row built from the backend's OWN orgTotal -- never a client-side re-sum,
 *  so the displayed total can never drift from the server's aggregate. */
function agentRollupRows(wire: CallcenterDailyRollupWire): AgentRollupRow[] {
  const rows: AgentRollupRow[] = wire.agents.map((a: CallcenterDailyRollupAgentRowView) => ({
    key: String(a.staffId),
    staffLabel: a.staffLabel,
    totalCount: a.activity.totalCount,
    distinctLeadsTouched: a.activity.distinctLeadsTouched,
  }));
  if (rows.length === 0) return rows;
  return [
    ...rows,
    {
      key: '__org_total',
      staffLabel: 'Org total',
      totalCount: wire.orgTotal.totalCount,
      distinctLeadsTouched: wire.orgTotal.distinctLeadsTouched,
      isTotal: true,
    },
  ];
}

// ---- Small inline icons (soon = outline clock, breached = filled alert) ----
// No icon system exists yet in this codebase for urgency (confirmed absent);
// these are minimal, dependency-free SVGs, never emoji.

function ClockOutlineIcon(): JSX.Element {
  return (
    <svg className="ccdr-urgency-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5V8l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function AlertFilledIcon(): JSX.Element {
  // An outline triangle (never a filled shape needing a contrasting
  // cutout) so the whole icon is ONE colour, currentColor -- no second
  // fill, no raw hex (design-system-conformance-guard RULE 1: colours are
  // minted once in dashboard.css :root, never a second literal).
  return (
    <svg className="ccdr-urgency-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 1.5 15 14H1L8 1.5Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <rect x="7.25" y="6" width="1.5" height="4" fill="currentColor" />
      <rect x="7.25" y="11" width="1.5" height="1.5" fill="currentColor" />
    </svg>
  );
}

/** Row-level urgency mark: text + icon, never a pill/chip (10b.1). Renders
 *  nothing for 'none' -- absence of decoration IS that step's treatment. */
function UrgencyMark({ step, ageMinutes }: { readonly step: UrgencyStep; readonly ageMinutes?: number }): JSX.Element | null {
  if (step === 'none') return null;
  const label = ageMinutes !== undefined ? formatLeakageAge(ageMinutes) : step === 'soon' ? 'Soon' : 'Breached';
  return (
    <span className={`ccdr-urgency ccdr-urgency--${step}`}>
      {step === 'soon' ? <ClockOutlineIcon /> : <AlertFilledIcon />}
      {label}
    </span>
  );
}

interface BreakdownRow {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly urgency?: UrgencyStep;
  readonly isTotal?: boolean;
}

function breakdownColumns(urgencyHeader?: string): DataTableColumn<BreakdownRow>[] {
  const cols: DataTableColumn<BreakdownRow>[] = [
    { key: 'label', header: 'Category', render: (r) => r.label },
  ];
  if (urgencyHeader) {
    cols.push({
      key: 'urgency',
      header: urgencyHeader,
      render: (r) => (r.urgency ? <UrgencyMark step={r.urgency} /> : null),
    });
  }
  cols.push({ key: 'count', header: 'Count', align: 'end', render: (r) => String(r.count) });
  return cols;
}

/** Appends a TOTALS row -- but only when there is something to total; an
 *  empty input stays empty so DataTable's own `rows.length === 0` -> empty
 *  state still fires (a lone "Total: 0" row would otherwise mask the
 *  legitimate zero-item day behind a table that looks non-empty). */
function withTotal(rows: readonly BreakdownRow[], totalLabel = 'Total'): BreakdownRow[] {
  if (rows.length === 0) return [];
  const total = rows.reduce((s, r) => s + r.count, 0);
  return [...rows, { key: '__total', label: totalLabel, count: total, isTotal: true }];
}

function activityTypeRowsToBreakdown(rows: readonly ActivityTypeRowView[]): BreakdownRow[] {
  const byCategory = new Map<string, number>();
  for (const r of rows) {
    byCategory.set(r.category, (byCategory.get(r.category) ?? 0) + r.count);
  }
  return [...byCategory.entries()].map(([category, count]) => ({
    key: category,
    label: ACTIVITY_CATEGORY_LABEL[category as keyof typeof ACTIVITY_CATEGORY_LABEL] ?? category,
    count,
  }));
}

function chaseRowsToBreakdown(rows: readonly ChaseClassRowView[]): BreakdownRow[] {
  return rows
    .filter((r) => r.count > 0)
    .map((r) => ({
      key: r.chaseClass,
      label: CHASE_CLASS_LABEL[r.chaseClass as ChaseClass],
      count: r.count,
    }));
}

function slaRowsToBreakdown(rows: readonly SlaClassSummaryView[]): BreakdownRow[] {
  return rows
    .filter((r) => r.count > 0)
    .map((r) => ({
      key: r.breachClass,
      label: classMeta(r.breachClass).title,
      count: r.count,
      urgency: urgencyStepForSeverity(r.severity),
    }));
}

const ENTRY_INLINE_CAP = 10;

function entryColumns(): DataTableColumn<ActivityEntryView>[] {
  return [
    {
      key: 'when',
      header: 'When',
      render: (e) =>
        new Intl.DateTimeFormat('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour: 'numeric',
          minute: '2-digit',
        }).format(new Date(e.occurredAtIso)),
    },
    { key: 'lead', header: 'Lead', render: (e) => e.displayName || 'Unnamed lead' },
    { key: 'clinic', header: 'Clinic', render: (e) => e.clinicLabel ?? '-' },
    { key: 'what', header: 'What happened', render: (e) => e.label },
  ];
}

export function LegacyDailyReportPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDate = searchParams.get(DAY_SCOPE_QUERY_KEY) ?? undefined;
  const [wire, setWire] = useState<CallcenterDailyReportWire | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [entriesDrawerOpen, setEntriesDrawerOpen] = useState(false);

  const load = useCallback(async () => {
    setState((prev) => (prev === 'ready' ? 'ready' : 'loading'));
    try {
      const data = await fetchCallcenterDailyReport(requestedDate ? { date: requestedDate } : {});
      setWire(data);
      setErrorDetail(null);
      setState('ready');
    } catch (err) {
      setErrorDetail(err instanceof Error ? err.message : 'Load failed');
      setState((prev) => (prev === 'ready' ? 'ready' : 'error'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedDate]);

  useEffect(() => {
    void load();
  }, [load]);

  const goToDate = (date: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set(DAY_SCOPE_QUERY_KEY, date);
    setSearchParams(next, { replace: false });
    setEntriesDrawerOpen(false);
  };

  const goToday = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete(DAY_SCOPE_QUERY_KEY);
    setSearchParams(next, { replace: false });
    setEntriesDrawerOpen(false);
  };

  if (state === 'error') {
    return (
      <div className="ac-band-flow" data-testid="ccdr-page">
        <PageHeader title="Daily report" subs={['Call centre - All times IST']} />
        <ErrorState
          title="This report failed to load"
          detail={errorDetail ?? 'The report wire did not answer.'}
          action={
            <AcButton size="sm" onClick={() => void load()}>
              Retry
            </AcButton>
          }
        />
      </div>
    );
  }

  if (state === 'loading' || wire === null) {
    return (
      <div className="ac-band-flow" data-testid="ccdr-page">
        <PageHeader title="Daily report" subs={['Call centre - All times IST']} />
        <LoadingState label="Loading the daily report" />
      </div>
    );
  }

  // ---- Manager rollup branch (activity 11) -- an EARLY RETURN, never mixed
  // with the per-agent JSX below: the backend already decided which shape
  // this caller gets (role-gated), so the two views are mutually exclusive
  // on this page, matching the wire's own disjoint shape.
  if (isRollupWire(wire)) {
    const rollupRows = agentRollupRows(wire);
    const orgBreakdown = withTotal(activityTypeRowsToBreakdown(wire.orgTotal.byType));
    return (
      <div className="ac-band-flow" data-testid="ccdr-page">
        <PageHeader title="Daily report" subs={['Call centre - Team rollup - All times IST']} />
        <p className="ccdr-exclusion-note" data-testid="ccdr-rollup-exclusion-note">
          {CCDR_ROLLUP_EXCLUSION_NOTE}
        </p>
        <p className="ccdr-exclusion-note ccdr-rollup-attribution-note" data-testid="ccdr-rollup-attribution-note">
          {CCDR_ROLLUP_ATTRIBUTION_NOTE}
        </p>

        <DateCommandBar
          date={wire.businessDate}
          today={wire.todayBusinessDate}
          onSelect={(date) => {
            if (isValidBusinessDate(date)) goToDate(date);
          }}
          onStep={(deltaDays) => goToDate(addBusinessDays(wire.businessDate, deltaDays))}
          onToday={goToday}
          label="Clinic day"
          testId="ccdr-day"
        />

        <div className="cockpit-tile-row" data-testid="ccdr-rollup-summary">
          <KpiTile
            label="Logged this day (whole team)"
            value={wire.orgTotal.totalCount}
            sub={`${wire.orgTotal.distinctLeadsTouched} distinct lead${wire.orgTotal.distinctLeadsTouched === 1 ? '' : 's'} touched`}
            testId="ccdr-rollup-tile-total"
          />
          <KpiTile
            label="Agents with assigned leads"
            value={wire.agents.length}
            sub={`across ${wire.orgLeadsTotalCount} assigned lead${wire.orgLeadsTotalCount === 1 ? '' : 's'}`}
            testId="ccdr-rollup-tile-agents"
          />
        </div>
        {wire.orgLeadsCapped ? (
          <p className="ccdr-exclusion-note" data-testid="ccdr-rollup-capped-note">
            The assigned-lead read hit its safety cap for this org -- some agents&apos; totals below may be
            incomplete. Contact engineering if this persists.
          </p>
        ) : null}

        <SectionBand
          title="By agent"
          basis={`${wire.agents.length} agent${wire.agents.length === 1 ? '' : 's'}`}
          testId="ccdr-rollup-agents-section"
        >
          <DataTable
            columns={agentRollupColumns()}
            rows={rollupRows}
            rowKey={(r) => r.key}
            rowClassName={(r) => (r.isTotal ? 'ccdr-row--total' : undefined)}
            label="Activity by agent"
            testId="ccdr-rollup-agents-table"
            empty={
              <EmptyState
                title="No agent currently holds an assigned lead."
                body="Nothing to roll up for this day -- no leads are assigned to any agent in scope."
              />
            }
          />
        </SectionBand>

        <SectionBand
          title="Worked this day - whole team"
          basis={`${wire.orgTotal.totalCount} logged`}
          testId="ccdr-rollup-activity-section"
        >
          <DataTable
            columns={breakdownColumns()}
            rows={orgBreakdown}
            rowKey={(r) => r.key}
            rowClassName={(r) => (r.isTotal ? 'ccdr-row--total' : undefined)}
            label="Activity by category - whole team"
            testId="ccdr-rollup-activity-table"
            empty={
              <EmptyState
                title="Nothing logged this day."
                body="No contact attempts, closures, qualifications or bookings were recorded across the team for this day."
              />
            }
          />
        </SectionBand>
      </div>
    );
  }

  const activityBreakdown = withTotal(activityTypeRowsToBreakdown(wire.activity.byType));
  const chaseBreakdown = wire.liveBacklog.chase ? withTotal(chaseRowsToBreakdown(wire.liveBacklog.chase.byClass)) : [];
  const slaBreakdown = wire.liveBacklog.slaBreach ? withTotal(slaRowsToBreakdown(wire.liveBacklog.slaBreach.byClass)) : [];
  const inlineEntries = wire.activity.entries.slice(0, ENTRY_INLINE_CAP);
  const moreEntries = wire.activity.entriesTotalCount - inlineEntries.length;

  return (
    <div className="ac-band-flow" data-testid="ccdr-page">
      <PageHeader title="Daily report" subs={['Call centre - All times IST']} />
      <p className="ccdr-exclusion-note" data-testid="ccdr-exclusion-note">
        {CCDR_EXCLUSION_NOTE}
      </p>

      <DateCommandBar
        date={wire.businessDate}
        today={wire.todayBusinessDate}
        onSelect={(date) => {
          if (isValidBusinessDate(date)) goToDate(date);
        }}
        onStep={(deltaDays) => goToDate(addBusinessDays(wire.businessDate, deltaDays))}
        onToday={goToday}
        label="Clinic day"
        testId="ccdr-day"
      />

      {/* ---- Summary strip: counts + the proportion that makes them meaningful ---- */}
      <div className="cockpit-tile-row" data-testid="ccdr-summary">
        <KpiTile
          label="Logged this day"
          value={wire.activity.totalCount}
          sub={
            wire.ownLeadsTotalCount > 0
              ? `across ${proportionLabel(wire.activity.distinctLeadsTouched, wire.ownLeadsTotalCount)} of your leads`
              : 'no leads currently assigned to you'
          }
          testId="ccdr-tile-activity"
        />
        {wire.liveBacklog.available && wire.liveBacklog.chase ? (
          <KpiTile
            label="Still open (chase)"
            value={wire.liveBacklog.chase.totalCount}
            sub={
              wire.ownLeadsTotalCount > 0
                ? `${proportionLabel(wire.liveBacklog.chase.totalCount, wire.ownLeadsTotalCount)} of your leads - right now`
                : 'right now'
            }
            tone={wire.liveBacklog.chase.totalCount > 0 ? 'warm' : 'ok'}
            testId="ccdr-tile-chase"
          />
        ) : null}
        {wire.liveBacklog.available && wire.liveBacklog.slaBreach ? (
          <KpiTile
            label="Breached SLA"
            value={wire.liveBacklog.slaBreach.totalCount}
            sub={
              wire.liveBacklog.slaBreach.totalCount > 0
                ? `across your ${wire.ownLeadsTotalCount} lead${wire.ownLeadsTotalCount === 1 ? '' : 's'} - right now`
                : 'right now'
            }
            tone={
              wire.liveBacklog.slaBreach.byClass.some((c) => c.severity === 'red')
                ? 'breach'
                : wire.liveBacklog.slaBreach.byClass.some((c) => c.severity === 'amber')
                  ? 'warm'
                  : 'ok'
            }
            testId="ccdr-tile-breach"
          />
        ) : null}
      </div>
      {!wire.liveBacklog.available ? (
        <p className="ccdr-exclusion-note" data-testid="ccdr-backlog-live-only-note">
          {CCDR_BACKLOG_LIVE_ONLY_NOTE}
        </p>
      ) : null}

      {/* ---- Activity breakdown (any day; TOTALS row) ---- */}
      <SectionBand
        title="Worked this day"
        basis={`${wire.activity.totalCount} logged`}
        testId="ccdr-activity-section"
      >
        <DataTable
          columns={breakdownColumns()}
          rows={activityBreakdown}
          rowKey={(r) => r.key}
          rowClassName={(r) => (r.isTotal ? 'ccdr-row--total' : undefined)}
          label="Activity by category"
          testId="ccdr-activity-table"
          empty={
            <EmptyState
              title="Nothing logged this day."
              body="No contact attempts, closures, qualifications or bookings were recorded on your leads for this day."
            />
          }
        />
      </SectionBand>

      {/* ---- Chase + SLA breakdowns (TODAY only) ---- */}
      {wire.liveBacklog.available && wire.liveBacklog.chase ? (
        <SectionBand
          title="Chase queue - still open"
          basis={`${wire.liveBacklog.chase.totalCount} open, right now`}
          testId="ccdr-chase-section"
        >
          <DataTable
            columns={breakdownColumns()}
            rows={chaseBreakdown}
            rowKey={(r) => r.key}
            rowClassName={(r) => (r.isTotal ? 'ccdr-row--total' : undefined)}
            label="Chase queue by class"
            testId="ccdr-chase-table"
            empty={<EmptyState title="Nothing stalling before the visit." body="No enquired-never-booked or booked-never-visited leads right now." />}
          />
        </SectionBand>
      ) : null}

      {wire.liveBacklog.available && wire.liveBacklog.slaBreach ? (
        <SectionBand
          title="Breached SLA"
          basis={`${wire.liveBacklog.slaBreach.totalCount} open, right now`}
          testId="ccdr-sla-section"
        >
          <DataTable
            columns={breakdownColumns('Urgency')}
            rows={slaBreakdown}
            rowKey={(r) => r.key}
            rowClassName={(r) => (r.isTotal ? 'ccdr-row--total' : r.urgency === 'breached' ? 'ccdr-row--breached' : undefined)}
            label="SLA breaches by class"
            testId="ccdr-sla-table"
            empty={<EmptyState title="No open SLA breaches." body="Nothing has crossed its SLA threshold right now." />}
          />
        </SectionBand>
      ) : null}

      {/* ---- The activity log itself (honest overflow) ---- */}
      <SectionBand
        title="Activity log"
        basis={`${wire.activity.entriesTotalCount} entr${wire.activity.entriesTotalCount === 1 ? 'y' : 'ies'}`}
        testId="ccdr-log-section"
      >
        <DataTable
          columns={entryColumns()}
          rows={inlineEntries}
          rowKey={(e) => `${e.safeLeadId}-${e.occurredAtIso}-${e.eventType}`}
          label="Activity log entries"
          testId="ccdr-activity-log"
          empty={<EmptyState title="Nothing logged this day." body="No activity entries for this day on your leads." />}
        />
        {moreEntries > 0 ? (
          <p className="omc-caption">
            <QuietAction size="sm" onClick={() => setEntriesDrawerOpen(true)} data-testid="ccdr-log-showall">
              {`Show all ${wire.activity.entriesTotalCount}`}
            </QuietAction>
            {wire.activity.entriesTotalCount > wire.activity.entries.length
              ? ` (showing the most recent ${wire.activity.entries.length})`
              : null}
          </p>
        ) : null}
        {entriesDrawerOpen ? (
          <DrawerShell eyebrow={dayLabel(wire.businessDate)} title="All activity" onClose={() => setEntriesDrawerOpen(false)} testId="ccdr-log-drawer">
            <DataTable
              columns={entryColumns()}
              rows={wire.activity.entries}
              rowKey={(e) => `${e.safeLeadId}-${e.occurredAtIso}-${e.eventType}`}
              label="All activity log entries"
              testId="ccdr-log-drawer-table"
            />
            {wire.activity.entriesTotalCount > wire.activity.entries.length ? (
              <p className="omc-caption">
                {`Showing the most recent ${wire.activity.entries.length} of ${wire.activity.entriesTotalCount}. Open the lead in Command center for its full record.`}
              </p>
            ) : null}
          </DrawerShell>
        ) : null}
      </SectionBand>
    </div>
  );
}


export default function CallCenterDailyReportPage(): JSX.Element {
  const me = useDashboardMeContext()?.me;
  return me?.role === 'call_center_manager' ? <PeriodReports /> : <LegacyDailyReportPage />;
}

import { fetchCallcenterWorkspace, fetchCallcenterPeriodReport, fetchCallcenterPeriodDetails, downloadCallcenterPeriodReport } from '@/dashboard/api';
import { reportJunkShareLabel, reportNumericPresentation, reportOutcomeTiles, reportSourceOptions, reportMonthRange, validReportRange, reportCountLabel, reportSnapshotQuery, type PeriodReportView, type PeriodMetric, type PeriodDetailView } from './callcenter-daily-report-view';
import '../callcenter-workspace.css';
import { reportAgentOptions } from '../callcenter-workspace-view';

/** Approved monthly report composition over existing server measures. */
export function PeriodReports({ summary = false, heading, workspaceNavigation, workspaceRefresh }: {summary?:boolean; heading?:ReactNode; workspaceNavigation?:ReactNode; workspaceRefresh?:ReactNode}): JSX.Element {
  const [params,setParams]=useSearchParams();
  const navigate=useNavigate();
  const today=new Intl.DateTimeFormat('en-CA',{timeZone:'Asia/Kolkata',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());
  const month=reportMonthRange(today);
  const rangeLabel=(date:string)=>new Intl.DateTimeFormat('en-GB',{day:'numeric',month:'short',year:'numeric',timeZone:'UTC'}).format(new Date(date+'T12:00:00Z'));
  const from=params.get('periodStart') ?? month.from, through=params.get('periodEnd') ?? month.through;
  const source=params.get('source') ?? '';
  const agent=params.get('agentId') ?? '';
  const active=params.get('reportView')==='outcomes'?'outcomes':'operations';
  const reference=params.get('reportView')==='reference';
  const rangeDialog=useRef<HTMLDialogElement>(null);
  const rangeTrigger=useRef<HTMLButtonElement>(null);
  function closeRange(): void {rangeDialog.current?.close();rangeTrigger.current?.focus();}
  function presetRange(days: number | null): void {
    setDraftFrom(days===null?month.from:new Date(Date.parse(today+'T12:00:00Z')-(days-1)*86400000).toISOString().slice(0,10));
    setDraftThrough(today);
  }
  const [draftFrom,setDraftFrom]=useState(from),[draftThrough,setDraftThrough]=useState(through);
  const [wire,setWire]=useState<PeriodReportView|null>(null),[loading,setLoading]=useState(true),[failed,setFailed]=useState(false),[retry,setRetry]=useState(0);
  const [detail,setDetail]=useState<PeriodDetailView|null>(null),[metric,setMetric]=useState<PeriodMetric|null>(null),[detailLoading,setDetailLoading]=useState(false),[detailError,setDetailError]=useState(false);
  const [junk,setJunk]=useState(false),[exporting,setExporting]=useState(false),[exportError,setExportError]=useState(false);
  const [sources,setSources]=useState<readonly string[]>([]);
  const [agents,setAgents]=useState<readonly {id:number;label:string}[]|null>(null);
  const [agentsLoading,setAgentsLoading]=useState(false),[agentsRetry,setAgentsRetry]=useState(0);
  const managerReport=wire?.scope.ownStaffId===null;
  useEffect(()=>{
    if(summary || !managerReport)return;
    let current=true;setAgents(null);setAgentsLoading(true);
    void fetchCallcenterWorkspace().then(view=>{if(current){setAgents(reportAgentOptions(view)?.map(agent=>({...agent,label:agent.label+(view.manager?.workload?.find(row=>row.staffUserId===agent.id)?.paused?' (paused)':'')}))??null);setSources(previous=>reportSourceOptions([...previous,...view.items.flatMap(row=>row.source?[row.source]:[])],source))}}).catch(()=>{if(current)setAgents(null)}).finally(()=>{if(current)setAgentsLoading(false)});
    return()=>{current=false};
  },[summary,managerReport,agentsRetry]);
  const detailRequest=useRef(0);
  const valid=validReportRange(from,through) && (!agent || /^[1-9]\d*$/.test(agent));
  useEffect(()=>{setDraftFrom(from);setDraftThrough(through)},[from,through]);
  useEffect(()=>{
    let current=true;detailRequest.current++;setWire(null);setDetail(null);setMetric(null);setFailed(false);setLoading(true);setExportError(false);setJunk(false);
    if(!valid){setLoading(false);return;}
    void fetchCallcenterPeriodReport({periodStart:from,periodEnd:through,...(source?{source}:{}),...(agent?{agentId:Number(agent)}:{})}).then(data=>{if(current){setWire(data);setSources(previous=>reportSourceOptions([...previous,...(data.sourceBreakdown?.rows.flatMap(row=>row.source?[row.source]:[])??[])],source))}}).catch(()=>{if(current)setFailed(true)}).finally(()=>{if(current)setLoading(false)});
    return()=>{current=false;detailRequest.current++};
  },[from,through,source,agent,retry,valid]);
  function update(patch:Record<string,string>){const next=new URLSearchParams(params);if('periodStart' in patch || 'periodEnd' in patch)next.delete('queueLead');for(const [key,value] of Object.entries(patch))value?next.set(key,value):next.delete(key);setParams(next);}
  async function inspect(key:PeriodMetric,cursor?:string){
    if(!wire)return;const request=++detailRequest.current;setMetric(key);setDetailLoading(true);setDetailError(false);if(!cursor)setDetail(null);
    try{const result=await fetchCallcenterPeriodDetails(reportSnapshotQuery(wire),key,cursor);if(request===detailRequest.current)setDetail(result)}catch{if(request===detailRequest.current)setDetailError(true)}finally{if(request===detailRequest.current)setDetailLoading(false)}
  }
  function closeInspection(): void {
    detailRequest.current++;setMetric(null);setDetail(null);setJunk(false);
    if(params.has('reportMetric')){const next=new URLSearchParams(params);next.delete('reportMetric');setParams(next,{replace:true});}
  }
  async function excel(){
    if(!wire?.exportable || loading)return;setExporting(true);setExportError(false);
    try{const file=await downloadCallcenterPeriodReport(reportSnapshotQuery(wire));const url=URL.createObjectURL(file.blob);const a=document.createElement('a');a.href=url;a.download=file.filename;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000)}catch{setExportError(true)}finally{setExporting(false)}
  }
  const attempt=wire?.firstActualAttempt.measure;
  const reportScope={periodStart:from,periodEnd:through,...(source?{source}:{}),...(agent?{agentId:agent}:{})};
  const returnView=summary?'outcomes':reference?(params.get('returnReportView')==='outcomes'?'outcomes':'operations'):active;
  const reportSelection={reportView:returnView};
  const reportLink='/dashboard/callcenter/daily-report?'+new URLSearchParams({...reportScope,...reportSelection}).toString();
  const sourceLink='/dashboard/callcenter/daily-report?'+new URLSearchParams({...reportScope,...reportSelection,reportView:'reference',returnReportView:returnView}).toString();
  const tiles=wire && (active==='outcomes'||summary||reference ? reportOutcomeTiles(wire,summary) : [
    {label:'Outbound calls',value:reportCountLabel(wire.recorded.providerCalls.outboundAttempts),key:'outbound_calls' as PeriodMetric},
    {label:'Connected calls',value:reportCountLabel(wire.recorded.providerCalls.connectedCalls),key:'connected_calls' as PeriodMetric},
    {label:'Missed inbound calls',value:reportCountLabel(wire.recorded.providerCalls.missedInboundCalls),key:'missed_calls' as PeriodMetric},
    {label:'Recorded activity',value:wire.recorded.recordedActivity.status==='complete'?String(wire.recorded.recordedActivity.summary?.totalCount??0):'Unavailable',key:'activity' as PeriodMetric},
    {label:'Awaiting first attempt',value:attempt?.noAttempt.toLocaleString('en-IN')??'Unavailable',key:'first_attempt' as PeriodMetric},
  ]);
  const activity=wire?.recorded.recordedActivity;
  const callGroups=wire?.recorded.providerCalls.currentAssignee;
  const assigneeIds=[...new Set([...(activity?.currentAssignee?.byAgent.map(row=>row.staffId)??[]),...(callGroups?.rows.flatMap(row=>row.staffId===null?[]:[row.staffId])??[])])].sort((a,b)=>a-b);
  const assigneeRows=[...assigneeIds.map(id=>({key:`agent:${id}`,kind:'agent',staffId:id,label:agents?.find(row=>row.id===id)?.label??`Agent #${id}`})),
    {key:'unassigned',kind:'unassigned',staffId:null,label:'Unassigned enquiries'},
    {key:'unlinked',kind:'unlinked',staffId:null,label:'Unlinked calls'},
  ].map(row=>{
    const calls=callGroups?.rows.find(group=>group.kind===row.kind&&group.staffId===row.staffId);
    const count=(key:'outboundAttempts'|'connectedCalls'|'missedInboundCalls')=>calls?reportCountLabel(calls[key]):callGroups?.status==='complete'?'0':'Unavailable';
    const assignedActivity=activity?.currentAssignee?.byAgent.find(group=>group.staffId===row.staffId)?.activity.totalCount??0;
    const unassignedActivity=(activity?.summary?.totalCount??0)-(activity?.currentAssignee?.orgTotal.totalCount??0);
    return {...row,outbound:count('outboundAttempts'),connected:count('connectedCalls'),missed:count('missedInboundCalls'),activity:row.kind==='unlinked'?'N/A':activity?.status!=='complete'?'Unavailable':String(row.kind==='unassigned'?unassignedActivity:assignedActivity)};
  });
  const numeric = (value: string | number) => {const display=reportNumericPresentation(value);return display.explanation?<span aria-label={display.explanation}>{display.text}</span>:display.text;};
  const partial = wire && [wire.bookingCount,wire.attendanceCount,wire.recorded.providerCalls.outboundAttempts,wire.recorded.providerCalls.connectedCalls,wire.recorded.providerCalls.missedInboundCalls].some(v=>v.status==='partial');
  const observed = wire ? new Intl.DateTimeFormat('en-IN',{timeZone:'Asia/Kolkata',day:'numeric',month:'short',hour:'numeric',minute:'2-digit'}).format(new Date(wire.firstActualAttempt.observedAt))+' IST' : 'Unavailable';
  // A summary deep link opens its permitted records after the scoped report loads.
  useEffect(()=>{if(summary||!wire)return;if(reference){detailRequest.current++;setMetric(null);setDetail(null);setJunk(false);return;}const key=params.get('reportMetric');if(key==='junk')setJunk(true);else if(['bookings','attendance','outbound_calls','connected_calls','missed_calls','activity','first_attempt'].includes(key??''))void inspect(key as PeriodMetric);},[wire,summary,reference,params.get('reportMetric')]);
  function selectMetric(key:PeriodMetric|null){
    const selection={reportView:active==='operations'&&!summary?'operations':'outcomes',reportMetric:key??'junk'};
    if(summary)navigate('/dashboard/callcenter/daily-report?'+new URLSearchParams({...reportScope,...selection}).toString());
    else if(key)void inspect(key);else setJunk(true);
  }
  const reportDefinitions=<><p>Current assignment does not identify who performed the action. Provider calls and lead activity are separate evidence.</p><p>Source is the recorded acquisition source of the authorized linked enquiry; attendance does not change it. Unknown source means missing source; unlinked appointments have no enquiry link. Bookings and arrivals are separate period events, not a conversion rate.</p> <p>Leads: {attempt?.totalCohort??'Unavailable'}. Junk leads: Unavailable. Percentage of leads: {attempt?.totalCohort===0?'N/A':'Unavailable'}.</p><p>Walk-ins are unavailable: this report records appointment attendance but has no verified direct-entry measure. Referral and campaign attribution remain separate from entry method.</p><p>A verified junk classification is not available. Unavailable values are not zero.</p><p>Calls count recorded provider events. First attempt requires confirmed outbound evidence; unknown history does not establish no attempt. Bookings and arrivals count separate period events.</p><p>{wire?.attribution}</p><p>Observed at: {wire ? new Intl.DateTimeFormat('en-IN', {timeZone:'Asia/Kolkata',dateStyle:'medium',timeStyle:'short'}).format(new Date(wire.firstActualAttempt.observedAt)) + ' IST' : 'Unavailable'}</p> </>;
  return <section className={summary?"cc-period-report cc-period-report--summary":"cc-period-report cc-period-report--full"} aria-label={summary?'Monthly report summary':'Reports'} data-testid="period-report">
    {reference && <Link to={reportLink}>Back to Reports</Link>}
    <div className="cc-monthly-heading">{heading}{!summary && <PageHeader eyebrow={managerReport?"Call center manager":"Call centre"} title={reference?'Source reference':'Reports'} subs={['Permitted team records']} />}
    <div className="cc-period-actions">{workspaceRefresh}<AcButton ref={rangeTrigger} className="cc-range-trigger" aria-haspopup="dialog" onClick={()=>{setDraftFrom(from);setDraftThrough(through);rangeDialog.current?.showModal();}}>{validReportRange(from,through)?`${rangeLabel(from)} – ${rangeLabel(through)}`:'Choose dates'} ▾</AcButton></div>
    <dialog ref={rangeDialog} className="cc-range-dialog" aria-labelledby="cc-range-title" onCancel={closeRange}>
      <form onSubmit={event=>{event.preventDefault();if(validReportRange(draftFrom,draftThrough)){update({periodStart:draftFrom,periodEnd:draftThrough});closeRange();}}}>
        <h2 id="cc-range-title">Choose dates</h2>
        <div className="cc-range-presets"><AcButton onClick={()=>presetRange(null)}>This month</AcButton><AcButton onClick={()=>presetRange(30)}>Last 30 days</AcButton><AcButton onClick={()=>presetRange(60)}>Last 60 days</AcButton></div>
        <div className="cc-range-calendars"><DateField id="report-from" label="From" value={draftFrom} onChange={setDraftFrom}/><DateField id="report-through" label="To" value={draftThrough} onChange={setDraftThrough}/></div>
        <p className="ac-text-caption">Inclusive · IST · up to 366 days</p>
        {!validReportRange(draftFrom,draftThrough)&&<p role="alert">Check the selected dates. Maximum 366 days.</p>}
        <div className="cc-range-actions"><AcButton onClick={closeRange}>Cancel</AcButton><AcButton type="submit" variant="primary" disabled={!validReportRange(draftFrom,draftThrough)}>Apply dates</AcButton></div>
      </form>
    </dialog>
    {!summary && reference && <AcButton disabled={!wire?.exportable||loading||exporting} onClick={()=>void excel()}>{exporting?'Exporting...':'Export Excel'}</AcButton>}
    </div>
    {summary && <div className="cc-monthly-navigation">{workspaceNavigation}</div>}
    {!valid && <p role="alert">Choose valid dates in order, up to 366 days, and a valid agent.</p>}

    {!summary && !reference && <>
    <div className="cc-report-toolbar">
      <nav aria-label="Report views"><AcButton aria-pressed={active==='operations'} onClick={()=>update({reportView:'operations'})}>Operations</AcButton><AcButton aria-pressed={active==='outcomes'} onClick={()=>update({reportView:'outcomes'})}>Outcomes</AcButton></nav>
      <div className="cc-report-controls cc-report-scope-controls">
        {managerReport && <div className="cc-report-agent-controls">
          <span className="cc-report-control-label">Agent</span>
          {!agentsLoading && agents!==null && <SelectorBar controls={[{key:'agent',label:'Report agent',value:agent,onChange:value=>update({agentId:value}),options:[
            {value:'',label:'All permitted agents'},
            ...(agent && !agents.some(row=>String(row.id)===agent)?[{value:agent,label:`Selected agent #${agent} - outside loaded workload`}]:[]),
            ...agents.map(row=>({value:String(row.id),label:row.label})),
          ]}]} />}
          {agentsLoading ? <span role="status">Loading agents...</span> : agents===null ? <><span>Agent list unavailable.</span><AcButton onClick={()=>setAgentsRetry(x=>x+1)}>Retry agents</AcButton></> : null}
        </div>}
        <label>Source<AcSelect aria-label="Report source" value={source} onChange={e=>update({source:e.target.value})}><option value="">All sources</option>{reportSourceOptions(sources,source).map(value=><option key={value} value={value}>{value}</option>)}</AcSelect></label>
        <AcButton disabled={!wire?.exportable||loading||exporting} onClick={()=>void excel()}>{exporting?'Exporting...':'Export Excel'}</AcButton>
      </div>
    </div>
    </>}
    {loading && <LoadingState label="Loading report" />}
    {failed && <ErrorState title="Report unavailable" detail="The selected report could not be loaded. No previous totals are shown." action={<AcButton onClick={()=>setRetry(x=>x+1)}>Retry</AcButton>} />}
    {wire && <>
      {!reference && <div className="cc-report-links"><Link to={sourceLink}>View source &amp; definitions ↗</Link>{!summary&&<span className="ac-text-caption">Original source reports unavailable</span>}</div>}
      {partial && <p className="ac-text-caption cc-report-qualification">Partial coverage · recorded counts only. <Link to={sourceLink}>Coverage details ↗</Link></p>}
      {!reference && <div className="cc-report-tiles">{tiles && tiles.map(tile=><KpiTile key={tile.label} size="hero" label={tile.label} value={numeric(tile.value)} sub={summary ? tile.label==='Leads'?'Recorded leads':tile.label==='Junk leads'?'Percentage of leads · '+reportJunkShareLabel(attempt?.totalCohort):tile.label==='Walk-ins'?'Entry method unavailable':'Bookings recorded in the period' : tile.key ? <>{tile.label==='Recorded activity'?<span>On currently assigned enquiries<br/></span>:tile.label==='Awaiting first attempt'?<span>Enquiries captured in this period<br/></span>:null}View records →</> : tile.label==='Junk leads'?'Percentage of leads: '+reportJunkShareLabel(attempt?.totalCohort):'Unavailable'} onClick={tile.label==='Call-to-booking rate'?undefined:tile.label==='Walk-ins'?()=>navigate(sourceLink):()=>selectMetric(tile.key)} />)}</div>}
      {!summary && !reference && active==='operations' && <div className="cc-report-operation-grid">
        <SectionShell eyebrow="Operations" title="Activity by current assignee" sub="Calls and follow-ups in this period"><div className="cc-report-section-body">
          <DataTable label="Activity by current assignee" columns={[
            {key:'agent',header:'Current assignee',render:r=>r.label},
            {key:'outbound',header:'Outbound',render:r=>numeric(r.outbound)},
            {key:'connected',header:'Connected',render:r=>numeric(r.connected)},
            {key:'missed',header:'Missed inbound',render:r=>numeric(r.missed)},
            {key:'activity',header:'Activity',render:r=>numeric(r.activity)},
          ]} rows={assigneeRows} rowKey={r=>r.key} empty={<EmptyState title="No recorded assignee activity" body="No call or lead activity groups are available in this period."/>}/>
          <div className="cc-report-footer"><AcButton className="cc-quiet" variant="ghost" onClick={()=>void inspect('activity')}>Open recorded activity →</AcButton></div>
        </div></SectionShell>
        <SectionShell eyebrow="First follow-up" title="New lead workload" sub={`${attempt?.totalCohort??'Unavailable'} enquiries captured in this period`}><div className="cc-report-section-body">
          <dl className="cc-first-attempt-summary" aria-label="First attempt cohort">
            {[
              {label:'Leads attempted',value:attempt?.attempted??'Unavailable'},
              {label:'Awaiting first attempt',detail:'Confirmed history only',value:attempt?.noAttempt??'Unavailable'},
              {label:'Attempt not known',value:attempt?.dataUnavailable??'Unavailable'},
              {label:'Mean first-attempt delay',detail:`${attempt?.delaySampleCount??0} measured`,value:attempt?.meanDelayMs==null?'Unavailable':`${Math.round(attempt.meanDelayMs/60000)} min`},
            ].map(row=><div key={row.label}><dt>{row.label}{row.detail&&<small>{row.detail}</small>}</dt><dd>{numeric(row.value)}</dd></div>)}
          </dl>
          <div className="cc-report-footer ac-text-caption">As of {observed}</div>
        </div></SectionShell>
      </div>}
      {!summary && !reference && active==='outcomes' && <SectionShell eyebrow="Outcomes" title="Leads by source" sub={source||'Bookings and recorded appointment attendance'}><div className="cc-report-section-body">{wire.sourceBreakdown?.status==='complete' ? <DataTable label="Leads by source" columns={[
        {key:'source',header:'Source',render:r=>r.key==='total'?'Total':r.kind==='source'?<AcButton className="cc-quiet" variant="ghost" size="sm" onClick={()=>update({source:r.source!})}>{r.source} →</AcButton>:r.kind==='unknown'?'Unknown source':'Unlinked appointments'},
        {key:'enquiries',header:'Leads',render:r=>r.newEnquiries.toLocaleString('en-IN')},
        {key:'bookings',header:'Bookings',render:r=>numeric(reportCountLabel(r.bookings))},
        {key:'arrivals',header:'Clinic visits',render:r=>numeric(reportCountLabel(r.attendance))},
        {key:'junk',header:'Junk leads',render:()=> numeric('Unavailable')},
        {key:'share',header:'Junk %',render:()=> numeric('Unavailable')},
      ]} rows={[...wire.sourceBreakdown.rows,{key:'total',kind:'unknown' as const,source:null,newEnquiries:attempt?.totalCohort??0,bookings:wire.bookingCount,attendance:wire.attendanceCount}]} rowKey={r=>r.key} empty={<EmptyState title="No source records" body="No enquiries or appointment evidence in this report scope."/>}/> : <p role="status">Source breakdown unavailable. No partial source totals are shown.</p>}
      <div className="cc-report-footer"><AcButton className="cc-quiet" variant="ghost" onClick={()=>void inspect('bookings')}>View bookings →</AcButton><AcButton className="cc-quiet" variant="ghost" onClick={()=>void inspect('attendance')}>View clinic visits →</AcButton></div>
      </div></SectionShell>}
      {reference && <SectionBand title="Source reference" basis="Coverage and provenance"><p>These figures come from authorized recorded enquiries, appointments, calls and activity for the displayed dates.</p>{reportDefinitions}<p>Original monthly source documents are unavailable from the current report service. No supplied aggregate has been substituted for operational records.</p><DataTable label="Report source availability" columns={[{key:'source',header:'Source',render:r=>r.source},{key:'state',header:'Coverage',render:r=>r.state==='complete_recorded_evidence'?'Recorded evidence available':'Unavailable or incomplete'}]} rows={Object.entries(wire.recorded.sourceStates).map(([source,state])=>({source,state}))} rowKey={r=>r.source}/></SectionBand>}
    </>}
    {exportError && <p role="alert">Export failed. Retry with the same displayed report.</p>}
    {junk && <DrawerShell title="Junk leads" onClose={closeInspection}>{reportDefinitions}</DrawerShell>}
    {metric && <DrawerShell title="Report records" onClose={closeInspection}>
      {detailLoading && <LoadingState label="Loading report records"/>}{detailError && <ErrorState title="Records unavailable" detail="Refresh or retry this report; no stale records are shown." action={<AcButton onClick={()=>void inspect(metric)}>Retry</AcButton>}/>}
      {!detailLoading&&!detailError&&detail && <><p>{detail.totalObservedRows} observed records</p><DataTable label="Report records" columns={[{key:'lead',header:'Enquiry',render:r=>r.leadId===null?'Unlinked':String(r.leadId)},{key:'when',header:'Occurred at',render:r=>r.occurredAt?new Intl.DateTimeFormat('en-IN',{timeZone:'Asia/Kolkata',dateStyle:'medium',timeStyle:'short'}).format(new Date(r.occurredAt)) + ' IST':'Unavailable'},{key:'state',header:'Evidence',render:r=>r.state}]} rows={detail.rows} rowKey={r=>String(r.id)} empty={<EmptyState title="No records in this period" body="Change the dates or filters to inspect another report."/>}/>{detail.nextCursor && <AcButton onClick={()=>void inspect(metric,detail.nextCursor!)}>Next records</AcButton>}</>}
    </DrawerShell>}
  </section>;
}
